import { MAPBOX_TOKEN } from '../utils/constants';

const MAPBOX_PLACES_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

/** Default types for general place search: address, place, locality, neighborhood, region, postcode. */
export const DEFAULT_PLACE_TYPES = 'address,place,locality,neighborhood,region,postcode';

/** Types for city/town-only search (e.g. store finder "search near this area"). */
export const CITY_TYPES = 'place,locality,neighborhood';

/**
 * Normalized place shape used across map search, store finder, and future features (e.g. camp essentials).
 * Keep in sync with mobile geocodingService and backend location payload (place_id, name, latitude, longitude).
 */
function normalizePlace(feature) {
  if (!feature) return null;
  const coords = Array.isArray(feature.center) ? feature.center : feature.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const context = feature.context || [];
  const state = extractContext(context, 'region');
  const country = extractContext(context, 'country');
  const place = {
    id: feature.id,
    name: feature.text || feature.place_name,
    label: feature.place_name,
    subtitle: [state, country].filter(Boolean).join(' • '),
    latitude: coords[1],
    longitude: coords[0],
  };
  const city = extractContext(context, 'place');
  const postcode = extractContext(context, 'postcode');
  if (city) place.city = city;
  if (state) place.state = state;
  if (postcode) place.postal_code = postcode;
  const addressLine = extractAddressLine(feature);
  if (addressLine) place.address = addressLine;
  const bbox = feature.bbox;
  if (Array.isArray(bbox) && bbox.length >= 4) {
    place.bounds = {
      sw: [bbox[0], bbox[1]],
      ne: [bbox[2], bbox[3]],
    };
  }
  return place;
}

function extractContext(contextList, target) {
  if (!Array.isArray(contextList)) return '';
  for (const entry of contextList) {
    if (!entry || typeof entry !== 'object') continue;
    if ((entry.id || '').startsWith(`${target}.`)) {
      if (target === 'region') {
        const code = entry.short_code || '';
        if (code.includes('-')) return code.split('-')[1].toUpperCase();
      }
      return entry.text || entry.short_code || '';
    }
  }
  return '';
}

/** Build address line from Mapbox context (address + street) when present. */
function extractAddressLine(feature) {
  const ctx = feature.context || [];
  const addr = ctx.find((e) => (e.id || '').startsWith('address.'));
  const street = ctx.find((e) => (e.id || '').startsWith('street.'));
  const parts = [feature.properties?.address, addr?.text, street?.text].filter(Boolean);
  return parts.join(' ').trim() || '';
}

/**
 * Parse a full US-style address string into street, city, state, postal_code.
 * Strips country (United States, USA, US). Use when API returns one combined line.
 */
export function parseFullAddressToComponents(fullAddress) {
  if (!fullAddress || typeof fullAddress !== 'string') {
    return { street: '', city: '', state: '', postal_code: '' };
  }
  let parts = fullAddress.split(',').map((s) => s.trim()).filter(Boolean);
  const countryLower = ['united states', 'usa', 'us', 'u.s.', 'u.s.a.'];
  if (parts.length > 1 && countryLower.includes(parts[parts.length - 1].toLowerCase())) {
    parts = parts.slice(0, -1);
  }
  if (parts.length === 0) {
    return { street: '', city: '', state: '', postal_code: '' };
  }
  const last = parts[parts.length - 1];
  const zipMatch = last.match(/\s*(\d{5}(?:-\d{4})?)\s*$/);
  const postal_code = zipMatch ? zipMatch[1] : '';
  const statePart = (zipMatch ? last.replace(/\s*\d{5}(?:-\d{4})?\s*$/, '') : last).trim();
  const state = statePart.length <= 2 ? statePart.toUpperCase() : statePart;
  let city = '';
  let street = '';
  if (parts.length >= 2) {
    city = parts[parts.length - 2];
    street = parts.slice(0, -2).join(', ');
  } else {
    street = last;
  }
  return { street, city, state, postal_code };
}

/**
 * Search for places (addresses, cities, regions). Use for map search and store finder.
 * @param {string} query - Search text (min 2 chars).
 * @param {{ limit?: number, types?: string, signal?: AbortSignal }} options - limit (default 8), types (default DEFAULT_PLACE_TYPES), AbortSignal.
 * @returns {Promise<Array<{ id, name, label, subtitle?, latitude, longitude, bounds? }>>}
 */
export async function searchPlaces(query, { limit = 8, types = DEFAULT_PLACE_TYPES, signal } = {}) {
  const trimmed = (query || '').trim();
  if (!trimmed || trimmed.length < 2 || !MAPBOX_TOKEN) return [];

  const encoded = encodeURIComponent(trimmed);
  const typesParam = (types || DEFAULT_PLACE_TYPES).replace(/\s/g, '');
  const url = `${MAPBOX_PLACES_BASE}/${encoded}.json?types=${typesParam}&limit=${Math.min(Math.max(limit, 1), 10)}&language=en&access_token=${MAPBOX_TOKEN}`;

  try {
    const response = await fetch(url, { signal });
    if (!response.ok) return [];
    const payload = await response.json();
    return (payload.features || []).map(normalizePlace).filter(Boolean);
  } catch (err) {
    if (err.name === 'AbortError') return [];
    return [];
  }
}

/**
 * Search for cities/towns only (backward-compatible). Use for store finder when you want area-level only.
 * @param {string} query
 * @param {{ signal?: AbortSignal }} options
 */
export async function searchCities(query, { signal } = {}) {
  return searchPlaces(query, { types: CITY_TYPES, limit: 6, signal });
}

/**
 * Reverse geocode coordinates to get address components. Use when we have lat/lng (e.g. from a pasted Google Maps URL).
 * @param {number} longitude
 * @param {number} latitude
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ address?: string, city?: string, state?: string, postal_code?: string, label: string } | null>}
 */
export async function reverseGeocode(longitude, latitude, { signal } = {}) {
  if (!MAPBOX_TOKEN || typeof longitude !== 'number' || typeof latitude !== 'number') return null;
  const url = `${MAPBOX_PLACES_BASE}/${longitude},${latitude}.json?types=address,place,locality,postcode&limit=1&language=en&access_token=${MAPBOX_TOKEN}`;
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) return null;
    const payload = await response.json();
    const feature = (payload.features || [])[0];
    if (!feature) return null;
    const context = feature.context || [];
    const city = extractContext(context, 'place');
    const state = extractContext(context, 'region');
    const postcode = extractContext(context, 'postcode');
    const addressLine = extractAddressLine(feature);
    const label = feature.place_name || '';
    if (addressLine) {
      return {
        address: addressLine,
        city: city || '',
        state: state || '',
        postal_code: postcode || '',
        label,
      };
    }
    if (label) {
      const parsed = parseFullAddressToComponents(label);
      return {
        address: parsed.street,
        city: city || parsed.city,
        state: state || parsed.state,
        postal_code: postcode || parsed.postal_code,
        label,
      };
    }
    return {
      address: '',
      city: city || '',
      state: state || '',
      postal_code: postcode || '',
      label: '',
    };
  } catch (err) {
    if (err.name === 'AbortError') return null;
    return null;
  }
}

/**
 * Parse a Google Maps place URL to extract place name and coordinates.
 * Supports URLs like: .../place/Chambers+Farm+.../@34.17,-86.85... or ...!3d34.1716197!4d-86.8603523
 * @param {string} urlOrText - Full URL or text containing it
 * @returns {{ name: string, latitude: number, longitude: number } | null}
 */
export function parseGoogleMapsPlaceUrl(urlOrText) {
  const str = String(urlOrText || '').trim();
  const placeMatch = str.match(/\/place\/([^/]+?)(?:\/|$)/);
  const name = placeMatch
    ? decodeURIComponent(placeMatch[1].replace(/\+/g, ' '))
    : '';
  const dataMatch = str.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (dataMatch) {
    const lat = parseFloat(dataMatch[1]);
    const lng = parseFloat(dataMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { name, latitude: lat, longitude: lng };
    }
  }
  const atMatch = str.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { name, latitude: lat, longitude: lng };
    }
  }
  return null;
}

const geocodingService = {
  searchPlaces,
  searchCities,
  reverseGeocode,
  parseGoogleMapsPlaceUrl,
};

export default geocodingService;

















